import React, { useState } from 'react';
import { FaFilter, FaMapMarkerAlt, FaBuilding } from 'react-icons/fa';

interface SitesMapFilterProps {
  projects: any[];
  onFilterChange: (filters: FilterState) => void;
}

interface FilterState {
  searchTerm: string;
  selectedProjects: string[];
  showDefaultOnly: boolean;
}

export const SitesMapFilter: React.FC<SitesMapFilterProps> = ({ projects, onFilterChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    selectedProjects: [],
    showDefaultOnly: false
  });

  const handleFilterChange = (newFilters: Partial<FilterState>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const toggleProject = (projectId: string) => {
    const newSelectedProjects = filters.selectedProjects.includes(projectId)
      ? filters.selectedProjects.filter(id => id !== projectId)
      : [...filters.selectedProjects, projectId];
    
    handleFilterChange({ selectedProjects: newSelectedProjects });
  };

  const clearFilters = () => {
    const clearedFilters = {
      searchTerm: '',
      selectedProjects: [],
      showDefaultOnly: false
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters = filters.searchTerm || filters.selectedProjects.length > 0 || filters.showDefaultOnly;

  return (
    <div className="absolute top-4 left-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 min-w-80">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <FaMapMarkerAlt className="text-blue-600" />
          <span className="font-semibold text-gray-800">Sites Map</span>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          <FaFilter className={`text-gray-600 ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Filter Content */}
      {isExpanded && (
        <div className="p-3 space-y-3">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Sites
            </label>
            <input
              type="text"
              placeholder="Search by site name or address..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange({ searchTerm: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Project Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Project
            </label>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {projects.map((project) => (
                <label key={project.id} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.selectedProjects.includes(project.id)}
                    onChange={() => toggleProject(project.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <FaBuilding className="text-gray-500 text-sm" />
                  <span className="text-sm text-gray-700 truncate">{project.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Default Sites Only */}
          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.showDefaultOnly}
                onChange={(e) => handleFilterChange({ showDefaultOnly: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Show default sites only</span>
            </label>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="w-full px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}; 